import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle, Circle, Buildings, Info } from "@phosphor-icons/react";

const BASIS = [{ value: "services", label: "Services" }, { value: "goods", label: "Goods" }];
const FIELD_MAP = { rbo: "rbo_filed", corporation_tax: "ct_registered", vat: "vat_registered", employer_paye: "employer_registered" };

export default function SetupWizard() {
  const [company, setCompany] = useState(null);
  const [reg, setReg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [c, r] = await Promise.all([api.get("/compliance/company"), api.get("/compliance/registrations")]);
    setCompany(c.data); setReg(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const persist = async (patch) => {
    const next = { ...company, ...patch };
    setCompany(next);
    setSaving(true);
    try { await api.put("/compliance/company", next); await load(); }
    finally { setSaving(false); }
  };

  if (!company || !reg) return <div className="text-white/40 text-sm">Loading…</div>;
  const vt = reg.vat_threshold;
  const vatColor = vt.status === "over" ? "#E63946" : vt.status === "near" ? "#FFB020" : "#00A86B";
  const pct = reg.total ? Math.round((reg.completed / reg.total) * 100) : 0;

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-2">Getting Started</div>
        <h1 className="font-display text-4xl">Startup setup & registrations</h1>
        <p className="text-white/60 text-sm mt-2">The registrations every Irish company needs — personalised to your details. Tick each off as you complete it; deadlines flow into your Compliance Calendar.</p>
      </div>

      {/* Progress */}
      <div className="glass rounded-2xl p-6" data-testid="setup-progress">
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow">Setup progress</div>
          <div className="mono text-sm text-white/70">{reg.completed}/{reg.total} complete</div>
        </div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#00A86B,#FFB020)" }} />
        </div>
      </div>

      {/* Company details */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4"><Buildings size={18} className="text-[#FFB020]" /><div className="eyebrow">Company details</div></div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><label>Trade name</label><input data-testid="setup-name" value={company.trade_name} onChange={(e) => setCompany({ ...company, trade_name: e.target.value })} onBlur={() => persist({})} /></div>
          <div><label>Incorporation date</label><input type="date" data-testid="setup-incorporation" value={company.incorporation_date} onChange={(e) => persist({ incorporation_date: e.target.value })} /></div>
          <div><label>Financial year end (MM-DD)</label><input placeholder="12-31" data-testid="setup-fye" value={company.financial_year_end} onChange={(e) => setCompany({ ...company, financial_year_end: e.target.value })} onBlur={() => persist({})} /></div>
          <div><label>Business basis (VAT)</label>
            <select data-testid="setup-basis" value={company.business_basis} onChange={(e) => persist({ business_basis: e.target.value })}>
              {BASIS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </div>
          <div><label>Est. 12-month turnover (€)</label><input type="number" data-testid="setup-turnover" value={company.annual_turnover} onChange={(e) => setCompany({ ...company, annual_turnover: e.target.value })} onBlur={() => persist({ annual_turnover: Number(company.annual_turnover || 0) })} /></div>
        </div>
      </div>

      {/* VAT threshold monitor */}
      <div className="glass rounded-2xl p-6" data-testid="vat-monitor">
        <div className="flex items-center justify-between mb-2">
          <div className="eyebrow">VAT registration monitor</div>
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: vatColor }}>
            {vt.status === "registered" ? "Registered" : vt.status === "over" ? "Threshold exceeded" : vt.status === "near" ? "Approaching threshold" : "Below threshold"}
          </span>
        </div>
        <div className="mono text-2xl">€{Number(vt.turnover).toLocaleString()} <span className="text-white/40 text-base">of €{Number(vt.threshold).toLocaleString()} ({vt.basis})</span></div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden mt-3">
          <div className="h-full rounded-full" style={{ width: `${Math.min(100, vt.pct)}%`, background: vatColor }} />
        </div>
        {vt.status === "near" && !company.vat_registered && <div className="text-xs text-[#FFB020] mt-2 flex items-center gap-1"><Info size={13} /> You&apos;re at {vt.pct}% of the VAT threshold — plan to register soon.</div>}
        {vt.status === "over" && !company.vat_registered && <div className="text-xs text-[#E63946] mt-2 flex items-center gap-1"><Info size={13} /> Turnover exceeds the threshold — you are likely obliged to register for VAT.</div>}
      </div>

      {/* Registration checklist */}
      <div className="space-y-3">
        <div className="eyebrow">Registration checklist</div>
        {reg.items.map((it) => {
          const toggleable = FIELD_MAP[it.id];
          return (
            <div key={it.id} className="glass rounded-xl p-5 flex items-start gap-4" data-testid={`reg-${it.id}`}>
              <button
                disabled={!toggleable || saving}
                onClick={() => toggleable && persist({ [toggleable]: !company[toggleable] })}
                className={toggleable ? "mt-0.5" : "mt-0.5 opacity-60 cursor-default"}
                data-testid={`reg-toggle-${it.id}`}
              >
                {it.done ? <CheckCircle size={24} weight="fill" className="text-[#00A86B]" /> : <Circle size={24} className="text-white/30" />}
              </button>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{it.label}</div>
                  {it.due ? <div className="text-xs text-white/45 mono">due ~{it.due}</div> : null}
                </div>
                <div className="text-sm text-white/55 mt-1">{it.guidance}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
